import { PartnersDeviceDoc, PartnersUserDoc, PartnersVehicleDoc } from '@gooduncles/gu-app-schema';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Button, ColorPicker, notification } from 'antd';
import { cloneDeep } from 'lodash-es';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { map } from 'rxjs';
import { permissionStateBall } from 'src/constants';

import { createOnRowDragEndHandler, onCellValueChangedWithUpdate } from 'src/utils/ag-grid-util';
import { errorObjectToString } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import {
  createPartnersVehicle,
  getPartnersDevices,
  observePartnersVehicle,
} from 'src/utils/firebase/firebase-short-cut';
import { updatePartnerColor } from 'src/utils/partners-util';

import { selectValidPartnersUsers } from 'src/redux/slices/partners';
import { useAppSelector } from 'src/redux/store';

import AgCellDeletePartnersUser from 'src/atomic-components/atoms/AgCell/AgCellDeletePartnersUser';

import classes from './PartnersUserTable.module.scss';

import DeleteVehicleButton from './DeleteVehicleButton/DeleteVehicleButton';

const partnersUserPath = 'partnersUser';
const partnersVehiclePath = 'partnersVehicle';
const onCellValueChanged = onCellValueChangedWithUpdate<PartnersUserDoc>('파트너 관리');
const onRowDragEnd = createOnRowDragEndHandler(partnersUserPath, 'deliveryPartnerStatus.sortKey');

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const PartnersUserGridTable: FC<{ rowData: PartnersUserDoc[]; partnersVehicles: PartnersVehicleDoc[] }> = ({
  rowData,
  partnersVehicles,
}) => {
  const columnDefs: ColDef<PartnersUserDoc>[] = useMemo(
    () => [
      {
        field: '_id',
        hide: true,
      },
      {
        width: 100,
        field: 'deliveryPartnerStatus.sortKey',
        headerName: '순서',
        rowDrag: true,
      },
      {
        width: 120,
        cellRenderer: 'agGroupCellRenderer',
        field: 'nickname',
        headerName: '담당자',
        editable: true,
        onCellValueChanged: (params) => onCellValueChanged(params, partnersUserPath, params.data._id),
      },
      {
        hide: true,
        field: 'name',
        headerName: '담당자(실명)',
        editable: true,
        onCellValueChanged: (params) => onCellValueChanged(params, partnersUserPath, params.data._id),
      },
      {
        width: 170,
        field: 'userTel',
        headerName: '전화번호',
        editable: true,
        onCellValueChanged: (params) => onCellValueChanged(params, partnersUserPath, params.data._id),
      },
      {
        width: 100,
        field: 'deliveryPartnerStatus.color',
        headerName: '색상',
        cellRenderer: (params: any) => {
          const { deliveryPartnerStatus, _id } = params.data as PartnersUserDoc;
          return (
            <ColorPicker
              value={deliveryPartnerStatus.color}
              onChangeComplete={(color) => updatePartnerColor(_id, color.toHexString())}
              defaultValue='#ffffff'
            />
          );
        },
      },
      {
        field: 'deliveryPartnerStatus.isActive',
        headerName: '상태',
        editable: true,
        cellEditorPopup: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: [true, false],
          cellRenderer: ({ value }: any) => <span>{value ? '🟢 활성' : '⚪️ 비활성'}</span>,
        },
        cellRenderer: ({ value }: any) => (
          <span>{value !== undefined ? (value ? '🟢 활성' : '⚪️ 비활성') : 'unknown'}</span>
        ),
        onCellValueChanged: (params) => onCellValueChanged(params, partnersUserPath, params.data._id),
      },
      {
        width: 140,
        field: 'deliveryPartnerStatus.vehicleId',
        headerName: '챠랑번호',
        editable: true,
        cellEditorPopup: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: [...partnersVehicles.map((vehicle) => vehicle._id), null],
          cellRenderer: ({ value }: any) => {
            if (value === null) return <span>-</span>;
            const vehicle = partnersVehicles.find((vehicle) => vehicle._id === value);
            return <span>{vehicle?.vehicleNumber ?? '?'}</span>;
          },
        },
        cellRenderer: ({ value }: any) => {
          if (value === null) return <span>-</span>;
          const vehicle = partnersVehicles.find((vehicle) => vehicle._id === value);
          return <span>{vehicle?.vehicleNumber ?? '?'}</span>;
        },
        onCellValueChanged: (params) =>
          onCellValueChanged(params, partnersUserPath, params.data._id, undefined, undefined, true),
      },
      {
        width: 240,
        field: 'email',
        headerName: '이메일',
      },
      {
        width: 120,
        field: '액션',
        headerName: '액션',
        cellRenderer: AgCellDeletePartnersUser,
        cellRendererParams: (params: any) => ({
          id: params.data._id,
          disabled: false,
        }),
      },
    ],
    [partnersVehicles]
  );

  /**
   * 파트너의 기기 정보를 보여주기 위한 렌더러 설정
   */
  const detailCellRendererParams = useMemo(() => {
    const detailColumnDefs: ColDef<PartnersDeviceDoc>[] = [
      {
        headerName: '업데이트 시각',
        field: 'updatedAt',
        valueFormatter: (params) => formatDate(new Date(params.value), 'yyyy-MM-dd HH:mm:ss'),
      },
      { headerName: '앱 버전', field: 'readableVersion', width: 120 },
      { headerName: '기기명', field: 'deviceName', width: 140 },
      { headerName: '제조사', field: 'manufacturer', width: 100 },
      { headerName: 'OS', field: 'systemName', width: 120 },
      { headerName: 'OS version', field: 'systemVersion', width: 130 },
      { headerName: 'deviceUniqueId', field: 'deviceUniqueId' },
      { headerName: 'token', field: 'token' },
      {
        headerName: '알림',
        width: 88,
        field: 'permissions.notification',
        valueGetter: (params) => permissionStateBall[params.data?.permissions.notification ?? ''] ?? '⚪️',
      },
      {
        headerName: '위치',
        width: 88,
        field: 'permissions.location',
        valueGetter: (params) => permissionStateBall[params.data?.permissions.location ?? ''] ?? '⚪️',
      },
      {
        headerName: '카메라',
        width: 96,
        field: 'permissions.camera',
        valueGetter: (params) => permissionStateBall[params.data?.permissions.camera ?? ''] ?? '⚪️',
      },
      {
        headerName: '사진첩',
        width: 96,
        field: 'permissions.location',
        valueGetter: (params) => permissionStateBall[params.data?.permissions.photo ?? ''] ?? '⚪️',
      },
      {
        headerName: '최초 설치 일자',
        field: 'firstInstallTime',
        valueFormatter: (params) => formatDate(new Date(params.value), 'yyyy-MM-dd HH:mm:ss'),
      },
    ];

    return {
      detailGridOptions: {
        columnDefs: detailColumnDefs,
        defaultColDef,
      },
      getDetailRowData: async (params: any) => {
        const partnersUserDoc = params.data as PartnersUserDoc;
        const devices = await getPartnersDevices([['partnerId', '==', partnersUserDoc._id]]);
        params.successCallback(devices);
      },
    };
  }, []);

  return (
    <div className='ag-theme-alpine height100 flex2'>
      <AgGridReact
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        context={{ partnersVehicles }}
        rowData={rowData}
        // rowId를 지정해주지 않으면 데이터 변경시 refresh가 발생한다.
        getRowId={(params) => params.data._id}
        // 편집 완료후 스크롤 이동을 막는다.
        suppressScrollOnNewData={true}
        // 순서 드래그
        rowDragManaged={true}
        rowDragMultiRow={true}
        rowSelection='multiple'
        animateRows={true}
        onRowDragEnd={onRowDragEnd}
        suppressMoveWhenRowDragging={true}
        enableCellTextSelection={true}
        // DetailRow
        keepDetailRows={true}
        masterDetail={true}
        detailRowAutoHeight={true}
        detailCellRendererParams={detailCellRendererParams}
      />
    </div>
  );
};

const PartnersVehicleGridTable: FC<{ rowData: PartnersVehicleDoc[] }> = ({ rowData }) => {
  const columnDefs: ColDef<PartnersUserDoc>[] = useMemo(
    () => [
      {
        field: '_id',
        hide: true,
      },
      {
        width: 160,
        field: 'vehicleNumber',
        headerName: '차량번호',
        editable: true,
        onCellValueChanged: (params) => onCellValueChanged(params, partnersVehiclePath, params.data._id),
      },
      {
        width: 180,
        field: 'vehicleModel',
        headerName: '차량 모델',
        editable: true,
        onCellValueChanged: (params) => onCellValueChanged(params, partnersVehiclePath, params.data._id),
      },
      {
        width: 120,
        headerName: '액션',
        cellRenderer: DeleteVehicleButton,
      },
    ],
    []
  );

  return (
    <div className='ag-theme-alpine height100 flex1'>
      <AgGridReact
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={rowData}
        // rowId를 지정해주지 않으면 데이터 변경시 refresh가 발생한다.
        getRowId={(params) => params.data._id}
        // 편집 완료후 스크롤 이동을 막는다.
        suppressScrollOnNewData={true}
        animateRows={true}
      />
    </div>
  );
};

const PartnersUserTable: FC = () => {
  const partnersUsers0 = useAppSelector(selectValidPartnersUsers);
  // redux의 불변성 객체가 ag-grid의 setValue에서 문제를 일으키기 때문에 깊은 복사를 진행한다.
  const partnersUsers = cloneDeep(partnersUsers0);
  const [partnersVehicles, setPartnersVehicles] = useState<PartnersVehicleDoc[]>([]);
  const [loading, setLoading] = useState(false);

  const addNewVehicle = useCallback(async () => {
    setLoading(true);
    try {
      await createPartnersVehicle({
        vehicleNumber: '신규 차량',
        vehicleModel: '신규 차량',
        vehicleImageUrl: null,
      });
    } catch (error) {
      console.error(error);
      const description = errorObjectToString(error);
      notification.error({
        message: '차량 추가 실패',
        description,
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const partnersVehicleSubscription = observePartnersVehicle()
      .pipe(map((docs) => docs.sort((a, b) => a.vehicleModel.localeCompare(b.vehicleModel))))
      .subscribe(setPartnersVehicles);
    return () => {
      partnersVehicleSubscription.unsubscribe();
    };
  }, []);

  return (
    <div className={classes.partnersTableContainer}>
      <div className={classes.buttons}>
        <Button loading={loading} onClick={addNewVehicle}>
          차량 추가
        </Button>
      </div>
      <div className={classes.tables}>
        <PartnersUserGridTable rowData={partnersUsers} partnersVehicles={partnersVehicles} />
        <PartnersVehicleGridTable rowData={partnersVehicles} />
      </div>
    </div>
  );
};

export default PartnersUserTable;
